/**
 * Tournament Store
 *
 * Manages Tournament, TournamentRound, and TournamentGroup state.
 * Coordinates with prioritization_sessions (via sessionStore) and
 * item_pools (via itemPoolStore) to advance winners between rounds.
 *
 * Architecture:
 *   - DB-first: Supabase is the system of record (Build Contract §2.1)
 *   - All durable state is persisted before local state is updated
 *   - Refresh-safe: loadTournaments / loadTournament reconstruct from DB
 *   - Advancement confirmation is gated (facilitator-confirmed before committing)
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, isSupabaseAvailable, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import type {
  Tournament,
  TournamentRound,
  TournamentGroup,
  AdvancementResult,
  CreateTournamentInput,
  CreateTournamentRoundInput,
  ConfirmAdvancementInput,
  TournamentNotificationPayload,
  AdvancementInput,
  RoundPrerequisite,
} from '../../types/prioritization/tournament';
import type { SessionRankingOutput } from '../../types/prioritization/session';

// ============================================================================
// State Shape
// ============================================================================

export interface TournamentStoreState {
  // Primary collections
  tournaments: Record<string, Tournament>;
  rounds: Record<string, TournamentRound>;
  groups: Record<string, TournamentGroup>;

  // Derived / UI state
  loading: boolean;
  error: string | null;

  // Pending advancement (facilitator review in progress)
  pendingAdvancement: {
    tournamentId: string;
    roundId: string;
    calculated: AdvancementResult;
  } | null;
}

// ============================================================================
// Actions Shape
// ============================================================================

export interface TournamentStoreActions {
  // ----- Tournaments -----

  /** Load all tournaments for a cycle (or workspace if cycleId is null). */
  loadTournaments: (cycleId?: string) => Promise<void>;

  /** Load a single tournament with all rounds and groups. */
  loadTournament: (tournamentId: string) => Promise<Tournament | null>;

  /** Create a new tournament in DRAFT state. */
  createTournament: (input: CreateTournamentInput) => Promise<Tournament>;

  /** Update tournament configuration (only allowed in DRAFT). */
  updateTournamentConfig: (
    tournamentId: string,
    config: Partial<Tournament['config']>
  ) => Promise<void>;

  /** Transition tournament to READY (locks configuration). */
  readyTournament: (tournamentId: string) => Promise<void>;

  /** Cancel a tournament (soft cancel; preserves audit trail). */
  cancelTournament: (tournamentId: string, reason?: string) => Promise<void>;

  // ----- Rounds -----

  /** Add a round to a DRAFT tournament. */
  addRound: (input: CreateTournamentRoundInput) => Promise<TournamentRound>;

  /**
   * Open a round (PENDING → OPEN).
   * Validates that all prerequisites are met; creates sessions for each group
   * (or one session for ungrouped rounds) via the session store.
   */
  openRound: (
    tournamentId: string,
    roundId: string
  ) => Promise<TournamentRound>;

  /**
   * Check and update prerequisites for a round.
   * Returns the updated round with met/unmet status on each prerequisite.
   */
  evaluatePrerequisites: (
    tournamentId: string,
    roundId: string
  ) => Promise<TournamentRound>;

  /**
   * Called when a session linked to this round reaches PUBLISHED.
   * Checks if all sessions in the round are now published, and if so
   * transitions the round to SESSIONS_COMPLETE and the tournament to
   * AWAITING_ADVANCE (calculating advancement in the process).
   */
  onSessionPublished: (
    sessionId: string,
    rankingOutput: SessionRankingOutput,
    tournamentId: string,
    roundId: string,
    groupId?: string
  ) => Promise<void>;

  /**
   * The facilitator says the round's sessions are done (OPEN → SESSIONS_COMPLETE,
   * tournament → AWAITING_ADVANCE) when no session publish reached
   * onSessionPublished. Written to the database and audited before the
   * local state changes; throws when the write fails.
   */
  markSessionsComplete: (tournamentId: string, roundId: string) => Promise<void>;

  /**
   * Calculate the advancement result for a completed round.
   * Stores the result in pendingAdvancement for facilitator review.
   * Does NOT commit advancing items until confirmAdvancement is called.
   */
  calculateAdvancement: (
    tournamentId: string,
    roundId: string
  ) => Promise<AdvancementResult>;

  /**
   * Facilitator confirms (and optionally reorders/overrides) advancement.
   * - Creates the advancingItemPool via the item pool store
   * - Marks the round COMPLETE
   * - Creates round N+1's pool reference
   * - If this was the last round: sets tournament.finalRankedItemIds,
   *   creates finalItemPool, transitions tournament to COMPLETE
   * - If autoAdvance: opens the next round immediately
   * - If not autoAdvance: transitions tournament to IN_PROGRESS so
   *   facilitator can open the next round manually
   */
  confirmAdvancement: (input: ConfirmAdvancementInput) => Promise<void>;

  /** Skip a round (PENDING or OPEN → SKIPPED). Requires facilitator rationale. */
  skipRound: (
    tournamentId: string,
    roundId: string,
    rationale: string
  ) => Promise<void>;

  // ----- Groups -----

  /**
   * Re-assign items across groups within a round (only when round is PENDING).
   * Used when the facilitator wants to rebalance groups before opening.
   */
  updateGroupItems: (
    groupId: string,
    itemIds: string[]
  ) => Promise<TournamentGroup>;

  /**
   * Re-assign participants to a group (only when round is PENDING or OPEN).
   */
  updateGroupParticipants: (
    groupId: string,
    participantIds: string[]
  ) => Promise<TournamentGroup>;

  /**
   * Update participants for a specific round.
   * Used when participantMode = 'per_round' to override the default participant list.
   */
  updateRoundParticipants: (
    roundId: string,
    participantIds: string[]
  ) => Promise<void>;

  /**
   * Update tournament-level participant configuration.
   * Sets the participantMode and defaultParticipantIds on config.
   */
  updateTournamentParticipants: (
    tournamentId: string,
    mode: 'fixed' | 'per_round' | 'open',
    defaultParticipantIds: string[]
  ) => Promise<void>;

  // ----- Results / Export -----

  /**
   * Export the tournament's final result as a published priority list.
   * Creates a priority_sets row from finalItemPoolId.
   * Returns the priority list ID.
   */
  exportFinalResult: (tournamentId: string) => Promise<string>;

  // ----- Selectors -----

  /** Get all tournaments for a given cycle. */
  getTournamentsForCycle: (cycleId: string) => Tournament[];

  /** Get all tournaments linked to a given track. */
  getTournamentsForTrack: (trackId: string) => Tournament[];

  /** Get the currently active round for a tournament. */
  getCurrentRound: (tournamentId: string) => TournamentRound | null;

  /** Get all rounds for a tournament, in order. */
  getRoundsForTournament: (tournamentId: string) => TournamentRound[];

  /** Get all groups for a round. */
  getGroupsForRound: (roundId: string) => TournamentGroup[];

  /**
   * Get the advancement summary for a completed round.
   * Returns the scores and ranks that drove advancement.
   */
  getRoundAdvancementSummary: (
    roundId: string
  ) => AdvancementResult | null;

  // ----- Notifications -----

  /** Dispatch a tournament notification event. */
  dispatchNotification: (payload: TournamentNotificationPayload) => void;
}

export type TournamentStore = TournamentStoreState & TournamentStoreActions;

// ============================================================================
// Advancement Calculator (pure function; no side effects)
// ============================================================================

/**
 * Calculates which items advance from a round given the configured rule.
 *
 * For per-group application:
 *   1. Apply the rule to each group's ranked list independently
 *   2. Collect all per-group advancing item IDs
 *   3. Merge and deduplicate, preserving intra-group rank order
 *   4. Apply maxAdvancing cap if set
 *
 * For global_merge:
 *   1. Merge all group rankings using normalized scores
 *   2. Re-rank merged list by normalized score (descending)
 *   3. Apply the rule to the unified list
 *   4. Apply maxAdvancing cap if set
 */
export function calculateAdvancementResult(
  input: AdvancementInput
): AdvancementResult {
  const { round, advancementRule, groupResults } = input;
  const rule = advancementRule;

  const groupIds = Object.keys(groupResults);
  const allItemIds = [
    ...new Set(
      groupIds.flatMap((gid) => groupResults[gid].rankedItemIds)
    ),
  ];

  let advancing: string[] = [];

  if (rule.groupApplication === 'global_merge' || groupIds.length === 1) {
    // Merge all scores, re-rank globally, apply rule to unified list
    const merged = mergeGroupScores(groupResults);
    advancing = applyRule(rule, merged.rankedItemIds, merged.scores);
  } else {
    // Per-group: apply rule to each group, then union the winners
    const perGroupAdvancing = groupIds.map((gid) => {
      const { rankedItemIds, scores } = groupResults[gid];
      return applyRule(rule, rankedItemIds, scores);
    });
    // Union preserving order: first-place across groups first
    advancing = unionPreservingOrder(perGroupAdvancing);
  }

  // Apply maxAdvancing cap
  if (rule.maxAdvancing != null && advancing.length > rule.maxAdvancing) {
    advancing = advancing.slice(0, rule.maxAdvancing);
  }

  // Apply minAdvancing floor
  if (rule.minAdvancing != null && advancing.length < rule.minAdvancing) {
    const remaining = allItemIds.filter((id) => !advancing.includes(id));
    advancing = [
      ...advancing,
      ...remaining.slice(0, rule.minAdvancing - advancing.length),
    ];
  }

  const advancingSet = new Set(advancing);
  const eliminated = allItemIds.filter((id) => !advancingSet.has(id));

  // Capture elimination rationale if needed
  const mergedScores = mergeGroupScores(groupResults).scores;
  const lastAdvancingScore =
    advancing.length > 0 ? (mergedScores[advancing[advancing.length - 1]] ?? 0) : 0;

  const eliminationRationale: AdvancementResult['eliminationRationale'] = {};
  eliminated.forEach((itemId, idx) => {
    eliminationRationale[itemId] = {
      round: round.roundNumber,
      roundLabel: round.label,
      rank: advancing.length + idx + 1,
      score: mergedScores[itemId],
      margin:
        mergedScores[itemId] != null
          ? lastAdvancingScore - mergedScores[itemId]
          : undefined,
    };
  });

  return {
    advancingItemIds: advancing,
    eliminatedItemIds: eliminated,
    eliminationRationale,
  };
}

// ============================================================================
// Internal helpers
// ============================================================================

function applyRule(
  rule: AdvancementInput['advancementRule'],
  rankedItemIds: string[],
  scores: Record<string, number>
): string[] {
  switch (rule.type) {
    case 'top_k':
      return rankedItemIds.slice(0, rule.k ?? rankedItemIds.length);
    case 'top_percent': {
      const count = Math.ceil(rankedItemIds.length * (rule.percent ?? 0.5));
      return rankedItemIds.slice(0, count);
    }
    case 'above_cut_line':
      // Scores above 50 normalized = above line (tool-dependent; use normalized score)
      return rankedItemIds.filter((id) => (scores[id] ?? 0) > 50);
    case 'facilitator_selects':
      // Return all; facilitator will trim in the confirmation step
      return [...rankedItemIds];
    default:
      return rankedItemIds;
  }
}

function mergeGroupScores(
  groupResults: AdvancementInput['groupResults']
): { rankedItemIds: string[]; scores: Record<string, number> } {
  // Collect all normalized scores; average when an item appears in multiple groups
  const scoreSums: Record<string, number> = {};
  const scoreCounts: Record<string, number> = {};

  for (const { rankedItemIds, scores } of Object.values(groupResults)) {
    for (const id of rankedItemIds) {
      scoreSums[id] = (scoreSums[id] ?? 0) + (scores[id] ?? 0);
      scoreCounts[id] = (scoreCounts[id] ?? 0) + 1;
    }
  }

  const averaged: Record<string, number> = {};
  for (const id of Object.keys(scoreSums)) {
    averaged[id] = scoreSums[id] / scoreCounts[id];
  }

  const rankedItemIds = Object.keys(averaged).sort(
    (a, b) => averaged[b] - averaged[a]
  );

  return { rankedItemIds, scores: averaged };
}

/**
 * Merge per-group winner lists by interleaving: take one item from each
 * group in turn (round-robin by rank position), deduplicating.
 */
function unionPreservingOrder(perGroupLists: string[][]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  const maxLen = Math.max(...perGroupLists.map((l) => l.length), 0);

  for (let i = 0; i < maxLen; i++) {
    for (const list of perGroupLists) {
      const item = list[i];
      if (item != null && !seen.has(item)) {
        seen.add(item);
        result.push(item);
      }
    }
  }

  return result;
}

// ============================================================================
// DB Row → TypeScript mappers
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToTournament(row: Record<string, any>): Tournament {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? undefined,
    cycleId: row.cycle_id ?? undefined,
    trackId: row.track_id ?? undefined,
    catchballLoopId: row.catchball_loop_id ?? undefined,
    config: row.config ?? {},
    rounds: [], // hydrated separately
    currentRoundIndex: row.current_round_index ?? undefined,
    state: row.state as Tournament['state'],
    finalRankedItemIds: row.final_ranked_item_ids ?? undefined,
    finalItemPoolId: row.final_item_pool_id ?? undefined,
    publishedPrioritySetId: row.published_priority_set_id ?? undefined,
    facilitatorId: row.facilitator_id,
    createdBy: row.created_by,
    workspaceId: row.workspace_id ?? undefined,
    itemIds: row.item_ids ?? [],
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
    cancelledAt: row.cancelled_at ? new Date(row.cancelled_at) : undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToRound(row: Record<string, any>): TournamentRound {
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    roundNumber: row.round_number,
    label: row.label ?? undefined,
    state: row.state as TournamentRound['state'],
    itemPoolId: row.item_pool_id ?? '',
    advancingK: row.advancing_k ?? undefined,
    sessionIds: row.session_ids ?? [],
    advancingItemIds: row.advancing_item_ids ?? undefined,
    eliminatedItemIds: row.eliminated_item_ids ?? undefined,
    advancingItemPoolId: row.advancing_item_pool_id ?? undefined,
    toolId: row.tool_id ?? undefined,
    toolConfig: row.tool_config ?? undefined,
    participantIds: row.participant_ids ?? undefined,
    prerequisites: row.prerequisites ?? [],
    opensAt: row.opens_at ? new Date(row.opens_at) : undefined,
    closesAt: row.closes_at ? new Date(row.closes_at) : undefined,
    completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useTournamentStore = create<TournamentStore>((set, get) => ({
  // ---- Initial state ----
  tournaments: {},
  rounds: {},
  groups: {},
  loading: false,
  error: null,
  pendingAdvancement: null,

  // ---- Selectors ----

  getTournamentsForCycle: (cycleId) =>
    Object.values(get().tournaments).filter((t) => t.cycleId === cycleId),

  getTournamentsForTrack: (trackId) =>
    Object.values(get().tournaments).filter((t) => t.trackId === trackId),

  getCurrentRound: (tournamentId) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament || tournament.currentRoundIndex == null) return null;
    const round = tournament.rounds[tournament.currentRoundIndex];
    return round ? (get().rounds[round.id] ?? null) : null;
  },

  getRoundsForTournament: (tournamentId) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament) return [];
    return tournament.rounds
      .map((r) => get().rounds[r.id])
      .filter(Boolean) as TournamentRound[];
  },

  getGroupsForRound: (roundId) =>
    Object.values(get().groups).filter((g) => g.roundId === roundId),

  getRoundAdvancementSummary: (roundId) => {
    const { pendingAdvancement } = get();
    if (pendingAdvancement?.roundId === roundId) {
      return pendingAdvancement.calculated;
    }
    // Could also read from audit events / persisted state
    return null;
  },

  // ---- Tournaments ----

  loadTournaments: async (cycleId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !isSupabaseAvailable()) return;
    set({ loading: true, error: null });
    try {
      const workspaceId = getCurrentWorkspaceId();
      let query = supabase.from('tournaments').select('*');
      if (workspaceId) query = query.eq('workspace_id', workspaceId);
      if (cycleId) query = query.eq('cycle_id', cycleId);
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;

      const tournaments: Record<string, Tournament> = {};
      for (const row of data ?? []) {
        const t = rowToTournament(row);
        tournaments[t.id] = t;
      }

      // Load all rounds for these tournaments in one query
      const tournamentIds = Object.keys(tournaments);
      const rounds: Record<string, TournamentRound> = {};
      if (tournamentIds.length > 0) {
        const { data: roundRows } = await supabase
          .from('tournament_rounds')
          .select('*')
          .in('tournament_id', tournamentIds)
          .order('round_number', { ascending: true });
        for (const row of roundRows ?? []) {
          const r = rowToRound(row);
          rounds[r.id] = r;
          // Attach round stub to tournament
          if (tournaments[r.tournamentId]) {
            tournaments[r.tournamentId].rounds.push(r);
          }
        }
      }

      set({ tournaments, rounds, loading: false });
    } catch (e) {
      set({ loading: false, error: String(e) });
    }
  },

  loadTournament: async (tournamentId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !isSupabaseAvailable()) {
      return get().tournaments[tournamentId] ?? null;
    }
    set({ loading: true, error: null });
    try {
      const { data: tRow, error: tErr } = await supabase
        .from('tournaments')
        .select('*')
        .eq('id', tournamentId)
        .single();
      if (tErr) throw tErr;

      const tournament = rowToTournament(tRow);

      const { data: roundRows } = await supabase
        .from('tournament_rounds')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('round_number', { ascending: true });
      const rounds: Record<string, TournamentRound> = { ...get().rounds };
      for (const row of roundRows ?? []) {
        const r = rowToRound(row);
        rounds[r.id] = r;
        tournament.rounds.push(r);
      }

      set(s => ({
        tournaments: { ...s.tournaments, [tournament.id]: tournament },
        rounds,
        loading: false,
      }));
      return tournament;
    } catch (e) {
      set({ loading: false, error: String(e) });
      return null;
    }
  },

  createTournament: async (input) => {
    set({ loading: true, error: null });
    try {
      const now = new Date();
      const workspaceId = input.workspaceId ?? getCurrentWorkspaceId() ?? undefined;

      const newTournament: Tournament = {
        id: crypto.randomUUID(),
        name: input.name,
        description: input.description,
        cycleId: input.cycleId,
        trackId: input.trackId,
        catchballLoopId: input.catchballLoopId,
        config: input.config,
        rounds: [],
        state: 'DRAFT',
        facilitatorId: input.facilitatorId,
        createdBy: input.facilitatorId,
        workspaceId,
        itemIds: input.itemIds ?? [],
        createdAt: now,
        updatedAt: now,
      };

      // Persist to DB first (Build Contract §2.1)
      const supabase = getSafeSupabaseClient();
      if (supabase && isSupabaseAvailable()) {
        const { error } = await supabase.from('tournaments').insert({
          id: newTournament.id,
          name: newTournament.name,
          description: newTournament.description ?? null,
          cycle_id: newTournament.cycleId ?? null,
          track_id: newTournament.trackId ?? null,
          catchball_loop_id: newTournament.catchballLoopId ?? null,
          config: newTournament.config,
          state: newTournament.state,
          facilitator_id: newTournament.facilitatorId,
          created_by: newTournament.createdBy,
          workspace_id: workspaceId ?? null,
          item_ids: newTournament.itemIds ?? [],
          created_at: now.toISOString(),
          updated_at: now.toISOString(),
        });
        if (error) throw error;
      }

      set((s) => ({
        tournaments: { ...s.tournaments, [newTournament.id]: newTournament },
        loading: false,
      }));
      return newTournament;
    } catch (e) {
      set({ loading: false, error: String(e) });
      throw e;
    }
  },

  updateTournamentConfig: async (tournamentId, config) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament || tournament.state !== 'DRAFT') {
      throw new Error('Can only update config in DRAFT state');
    }
    const now = new Date();
    const updated = {
      ...tournament,
      config: { ...tournament.config, ...config },
      updatedAt: now,
    };
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error } = await supabase.from('tournaments').update({ config: updated.config, updated_at: now.toISOString() }).eq('id', tournamentId);
      if (error) {
        console.error('[TournamentStore] Failed to update tournament config:', error.message);
        dispatchStoreError('Tournament', 'update config', error.message);
        return;
      }
    }
    set((s) => ({
      tournaments: { ...s.tournaments, [tournamentId]: updated },
    }));
  },

  readyTournament: async (tournamentId) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament) throw new Error('Tournament not found');
    if (tournament.state !== 'DRAFT') throw new Error('Must be in DRAFT');

    const { config } = tournament;
    if (!config.sourceItemPoolId) throw new Error('sourceItemPoolId required');
    if (!config.advancementRule) throw new Error('advancementRule required');
    if (config.criteriaMode === 'inherited' && !config.sharedCriteriaConfig) {
      throw new Error('sharedCriteriaConfig required when criteriaMode=inherited');
    }

    const now = new Date();
    const updated: Tournament = { ...tournament, state: 'READY', updatedAt: now };
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error } = await supabase.from('tournaments').update({ state: 'READY', updated_at: now.toISOString() }).eq('id', tournamentId);
      if (error) throw error;
    }
    set((s) => ({ tournaments: { ...s.tournaments, [tournamentId]: updated } }));
  },

  cancelTournament: async (tournamentId, reason) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament || tournament.state === 'COMPLETE') {
      throw new Error('Cannot cancel a completed tournament');
    }
    const now = new Date();
    const updated: Tournament = { ...tournament, state: 'CANCELLED', cancelledAt: now, updatedAt: now };
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error: cancelError } = await supabase.from('tournaments').update({ state: 'CANCELLED', cancelled_at: now.toISOString(), updated_at: now.toISOString() }).eq('id', tournamentId);
      if (cancelError) {
        console.error('[TournamentStore] Failed to cancel tournament:', cancelError.message);
        dispatchStoreError('Tournament', 'cancel tournament', cancelError.message);
        return;
      }
      const { error: auditError } = await supabase.from('tournament_audit_events').insert({
        tournament_id: tournamentId,
        event_type: 'TOURNAMENT_CANCELLED',
        actor_id: tournament.facilitatorId,
        payload: { reason },
        created_at: now.toISOString(),
      });
      if (auditError) {
        console.error('[TournamentStore] Failed to insert cancel audit event:', auditError.message);
      }
    }
    set((s) => ({ tournaments: { ...s.tournaments, [tournamentId]: updated } }));
    get().dispatchNotification({
      event: 'TOURNAMENT_CANCELLED',
      tournamentId,
      tournamentName: tournament.name,
      recipientIds: [tournament.facilitatorId],
    });
  },

  // ---- Rounds ----

  addRound: async (input) => {
    const tournament = get().tournaments[input.tournamentId];
    if (!tournament || tournament.state !== 'DRAFT') {
      throw new Error('Can only add rounds in DRAFT state');
    }
    const now = new Date();
    const round: TournamentRound = {
      id: crypto.randomUUID(),
      tournamentId: input.tournamentId,
      roundNumber: input.roundNumber,
      label: input.label,
      state: 'PENDING',
      itemPoolId: input.itemPoolId,
      advancingK: input.advancingK,
      sessionIds: [],
      groups: input.groups?.map((g) => ({
        ...g,
        id: crypto.randomUUID(),
        roundId: '',   // filled after round persisted
        tournamentId: input.tournamentId,
        advancingItemIds: [],
        scores: {},
      })),
      criteriaConfig: input.criteriaConfig,
      toolId: input.toolId,
      toolConfig: input.toolConfig,
      participantIds: input.participantIds,
      prerequisites: (input.prerequisites ?? []).map((p) => ({
        ...p,
        met: false,
      })),
      opensAt: input.opensAt,
      closesAt: input.closesAt,
      createdAt: now,
      updatedAt: now,
    };
    // Persist to DB (Build Contract §2.1)
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error } = await supabase.from('tournament_rounds').insert({
        id: round.id,
        tournament_id: round.tournamentId,
        round_number: round.roundNumber,
        label: round.label ?? null,
        state: round.state,
        item_pool_id: round.itemPoolId,
        advancing_k: round.advancingK ?? null,
        session_ids: round.sessionIds,
        tool_id: round.toolId ?? null,
        tool_config: round.toolConfig ?? null,
        participant_ids: round.participantIds ?? [],
        prerequisites: round.prerequisites,
        opens_at: round.opensAt?.toISOString() ?? null,
        closes_at: round.closesAt?.toISOString() ?? null,
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
      });
      if (error) throw error;
    }

    set((s) => ({
      rounds: { ...s.rounds, [round.id]: round },
      tournaments: {
        ...s.tournaments,
        [input.tournamentId]: {
          ...tournament,
          rounds: [...tournament.rounds, round],
          updatedAt: now,
        },
      },
    }));
    return round;
  },

  evaluatePrerequisites: async (tournamentId, roundId) => {
    const round = get().rounds[roundId];
    if (!round) throw new Error('Round not found');

    const evaluated = await Promise.all(
      round.prerequisites.map(async (prereq) => {
        switch (prereq.type) {
          case 'PREVIOUS_ROUND_COMPLETE': {
            const rounds = get().getRoundsForTournament(tournamentId);
            const priorRound = rounds.find(
              (r) => r.roundNumber === round.roundNumber - 1
            );
            return {
              ...prereq,
              met: priorRound == null || priorRound.state === 'COMPLETE',
              metAt: priorRound?.completedAt,
            };
          }
          case 'FACILITATOR_UNLOCK':
            // Already marked met by facilitator action; retain
            return prereq;
          case 'ITEM_READINESS':
          case 'VALUES_SUFFICIENT':
          case 'PARTICIPANT_CONFIRMED': {
            // Roadmap S-14: checked against the database. A check that cannot be
            // read stays unmet (fail closed), so a round never opens on a guess.
            const met = await checkRoundPrerequisite(prereq, round, roundItemIds(get(), tournamentId, round));
            return { ...prereq, met, metAt: met ? new Date() : undefined };
          }
          default:
            return prereq;
        }
      })
    );

    const updated: TournamentRound = {
      ...round,
      prerequisites: evaluated,
      updatedAt: new Date(),
    };
    set((s) => ({ rounds: { ...s.rounds, [roundId]: updated } }));
    return updated;
  },

  openRound: async (tournamentId, roundId) => {
    const round = await get().evaluatePrerequisites(tournamentId, roundId);
    const unmet = round.prerequisites.filter((p) => !p.met);
    if (unmet.length > 0) {
      throw new Error(
        `Prerequisites not met: ${unmet.map((p) => p.type).join(', ')}`
      );
    }

    const now = new Date();
    const updated: TournamentRound = { ...round, state: 'OPEN', updatedAt: now };
    const tournament = get().tournaments[tournamentId];

    // Persist round state + tournament state to DB (Build Contract §2.1)
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      await supabase.from('tournament_rounds').update({ state: 'OPEN', updated_at: now.toISOString() }).eq('id', roundId);
      await supabase.from('tournaments').update({
        state: 'IN_PROGRESS',
        current_round_index: round.roundNumber - 1,
        updated_at: now.toISOString(),
      }).eq('id', tournamentId);
      await supabase.from('tournament_audit_events').insert({
        tournament_id: tournamentId,
        round_id: roundId,
        event_type: 'ROUND_OPENED',
        actor_id: tournament.facilitatorId,
        payload: { roundNumber: round.roundNumber, roundLabel: round.label },
        created_at: now.toISOString(),
      });
    }

    set((s) => ({
      rounds: { ...s.rounds, [roundId]: updated },
      tournaments: {
        ...s.tournaments,
        [tournamentId]: {
          ...tournament,
          state: 'IN_PROGRESS',
          currentRoundIndex: round.roundNumber - 1,
          updatedAt: now,
        },
      },
    }));
    get().dispatchNotification({
      event: 'ROUND_OPENED',
      tournamentId,
      tournamentName: tournament.name,
      roundId,
      roundNumber: round.roundNumber,
      roundLabel: round.label,
      recipientIds: round.participantIds ?? tournament.config.defaultParticipantIds ?? [],
    });
    return updated;
  },

  markSessionsComplete: async (tournamentId, roundId) => {
    const round = get().rounds[roundId];
    const tournament = get().tournaments[tournamentId];
    if (!round || !tournament) throw new Error('Round not found');
    if (round.state !== 'OPEN') throw new Error('Only an open round can be marked complete');

    const now = new Date();
    // Persist round state + tournament state to DB (Build Contract §2.1)
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error: roundError } = await supabase
        .from('tournament_rounds')
        .update({ state: 'SESSIONS_COMPLETE', updated_at: now.toISOString() })
        .eq('id', roundId);
      if (roundError) throw new Error(roundError.message);
      const { error: tournamentError } = await supabase
        .from('tournaments')
        .update({ state: 'AWAITING_ADVANCE', updated_at: now.toISOString() })
        .eq('id', tournamentId);
      if (tournamentError) throw new Error(tournamentError.message);
      await supabase.from('tournament_audit_events').insert({
        tournament_id: tournamentId,
        round_id: roundId,
        event_type: 'ROUND_SESSIONS_COMPLETE',
        actor_id: tournament.facilitatorId,
        payload: { roundNumber: round.roundNumber, roundLabel: round.label, manual: true },
        created_at: now.toISOString(),
      });
    }

    set((s) => ({
      rounds: { ...s.rounds, [roundId]: { ...round, state: 'SESSIONS_COMPLETE', updatedAt: now } },
      tournaments: {
        ...s.tournaments,
        [tournamentId]: { ...tournament, state: 'AWAITING_ADVANCE', updatedAt: now },
      },
    }));
  },

  onSessionPublished: async (
    _sessionId,
    rankingOutput,
    tournamentId,
    roundId,
    groupId
  ) => {
    const round = get().rounds[roundId];
    if (!round) return;

    // Update scores on the group if applicable
    if (groupId) {
      const group = get().groups[groupId];
      if (group) {
        const updatedGroup: TournamentGroup = {
          ...group,
          scores: rankingOutput.normalizedScores,
          updatedAt: new Date(),
        };
        set((s) => ({ groups: { ...s.groups, [groupId]: updatedGroup } }));
      }
    }

    // Check if all sessions for this round have now published
    const allGroups = get().getGroupsForRound(roundId);
    const allSessionsPublished = allGroups.length > 0
      ? allGroups.every((g) => Object.keys(g.scores ?? {}).length > 0)
      : round.sessionIds.length > 0; // single-session round: the one session just published

    if (allSessionsPublished) {
      // Transition round to SESSIONS_COMPLETE
      const updatedRound: TournamentRound = {
        ...round,
        state: 'SESSIONS_COMPLETE',
        updatedAt: new Date(),
      };
      set((s) => ({ rounds: { ...s.rounds, [roundId]: updatedRound } }));

      // Calculate advancement and put it in pendingAdvancement
      await get().calculateAdvancement(tournamentId, roundId);

      // Transition tournament to AWAITING_ADVANCE
      const tournament = get().tournaments[tournamentId];
      set((s) => ({
        tournaments: {
          ...s.tournaments,
          [tournamentId]: {
            ...tournament,
            state: 'AWAITING_ADVANCE',
            updatedAt: new Date(),
          },
        },
      }));

      get().dispatchNotification({
        event: 'ADVANCEMENT_AWAITING_CONFIRMATION',
        tournamentId,
        tournamentName: tournament.name,
        roundId,
        roundNumber: round.roundNumber,
        roundLabel: round.label,
        recipientIds: [tournament.facilitatorId],
      });
    }
  },

  calculateAdvancement: async (tournamentId, roundId) => {
    const round = get().rounds[roundId];
    const tournament = get().tournaments[tournamentId];
    if (!round || !tournament) throw new Error('Round or tournament not found');

    const allGroups = get().getGroupsForRound(roundId);
    // Use per-round k override when available; fall back to tournament-level rule
    const advancementRule = round.advancingK != null
      ? { ...tournament.config.advancementRule, type: 'top_k' as const, k: round.advancingK }
      : tournament.config.advancementRule;

    const groupResults: AdvancementInput['groupResults'] = {};
    if (allGroups.length > 0) {
      for (const group of allGroups) {
        // Reconstruct ranked order from scores
        const scores = group.scores ?? {};
        const rankedItemIds = [...group.itemIds].sort(
          (a, b) => (scores[b] ?? 0) - (scores[a] ?? 0)
        );
        groupResults[group.id] = { rankedItemIds, scores };
      }
    } else {
      // Single-session round: the session's saved result (S-14).
      const output = await singleSessionResult(round.sessionIds?.[0]);
      groupResults['__single__'] = output ?? {
        rankedItemIds: round.advancingItemIds ?? [],
        scores: {},
      };
    }

    const result = calculateAdvancementResult({
      round,
      advancementRule,
      groupResults,
    });

    set({ pendingAdvancement: { tournamentId, roundId, calculated: result } });
    return result;
  },

  confirmAdvancement: async ({ tournamentId, roundId, advancingItemIds, allItemIds: inputAllItemIds, rationale }) => {
    const tournament = get().tournaments[tournamentId];
    const round = get().rounds[roundId];
    if (!tournament || !round) throw new Error('Not found');

    const now = new Date();

    // Determine which items were eliminated.
    // Priority: caller-supplied allItemIds > groups > empty (shouldn't happen)
    const advancingSet = new Set(advancingItemIds);
    const allGroups = get().getGroupsForRound(roundId);
    const allItemIds = inputAllItemIds
      ?? (allGroups.length > 0 ? [...new Set(allGroups.flatMap((g) => g.itemIds))] : []);
    const eliminatedItemIds = allItemIds.filter((id) => !advancingSet.has(id));

    const advancingItemPoolId = crypto.randomUUID(); // synthetic pool ID (MVP)

    const isLastRound =
      tournament.config.roundCount != null
        ? round.roundNumber >= tournament.config.roundCount
        : advancingItemIds.length <= 1;

    const updatedRound: TournamentRound = {
      ...round,
      state: 'COMPLETE',
      advancingItemIds,
      eliminatedItemIds,
      advancingItemPoolId,
      completedAt: now,
      updatedAt: now,
    };

    let updatedTournament: Tournament;

    if (isLastRound) {
      // Tournament is done
      const finalItemPoolId = advancingItemPoolId;
      updatedTournament = {
        ...tournament,
        state: 'COMPLETE',
        finalRankedItemIds: advancingItemIds,
        finalItemPoolId,
        completedAt: now,
        updatedAt: now,
      };
    } else {
      // Create next round if autoAdvance
      if (tournament.config.autoAdvance) {
        await get().addRound({
          tournamentId,
          roundNumber: round.roundNumber + 1,
          itemPoolId: advancingItemPoolId,
          prerequisites: [{ type: 'PREVIOUS_ROUND_COMPLETE' }],
        });
      }
      updatedTournament = {
        ...tournament,
        state: tournament.config.autoAdvance ? 'IN_PROGRESS' : 'IN_PROGRESS',
        updatedAt: now,
      };
    }

    // Auto-mark PREVIOUS_ROUND_COMPLETE prerequisite on the next pending round
    // so its "Open Round" button becomes enabled immediately.
    const allRounds = get().getRoundsForTournament(tournamentId);
    const nextRound = allRounds.find(r => r.roundNumber === round.roundNumber + 1);
    const updatedNextRound = nextRound
      ? {
          ...nextRound,
          prerequisites: nextRound.prerequisites.map(p =>
            p.type === 'PREVIOUS_ROUND_COMPLETE' ? { ...p, met: true, metAt: now } : p
          ),
          updatedAt: now,
        }
      : null;

    // Persist all state changes to DB (Build Contract §2.1)
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      // Update completed round
      await supabase.from('tournament_rounds').update({
        state: 'COMPLETE',
        advancing_item_ids: advancingItemIds,
        eliminated_item_ids: eliminatedItemIds,
        advancing_item_pool_id: advancingItemPoolId,
        completed_at: now.toISOString(),
        updated_at: now.toISOString(),
      }).eq('id', roundId);

      // Update tournament state
      const tUpdate: Record<string, unknown> = {
        state: updatedTournament.state,
        updated_at: now.toISOString(),
      };
      if (isLastRound) {
        tUpdate.final_ranked_item_ids = advancingItemIds;
        tUpdate.final_item_pool_id = updatedTournament.finalItemPoolId ?? null;
        tUpdate.completed_at = now.toISOString();
      }
      await supabase.from('tournaments').update(tUpdate).eq('id', tournamentId);

      // Mark next round's prereq as met in DB
      if (updatedNextRound) {
        await supabase.from('tournament_rounds').update({
          prerequisites: updatedNextRound.prerequisites,
          updated_at: now.toISOString(),
        }).eq('id', updatedNextRound.id);
      }

      // Audit event
      await supabase.from('tournament_audit_events').insert({
        tournament_id: tournamentId,
        round_id: roundId,
        event_type: updatedTournament.state === 'COMPLETE' ? 'TOURNAMENT_COMPLETE' : 'ROUND_COMPLETE',
        actor_id: tournament.facilitatorId,
        payload: {
          advancingItemIds,
          eliminatedItemIds,
          rationale,
          roundNumber: round.roundNumber,
          roundLabel: round.label,
        },
        created_at: now.toISOString(),
      });
    }

    set((s) => ({
      rounds: {
        ...s.rounds,
        [roundId]: updatedRound,
        ...(updatedNextRound ? { [updatedNextRound.id]: updatedNextRound } : {}),
      },
      tournaments: { ...s.tournaments, [tournamentId]: updatedTournament },
      pendingAdvancement: null,
    }));

    get().dispatchNotification({
      event: updatedTournament.state === 'COMPLETE' ? 'TOURNAMENT_COMPLETE' : 'ROUND_COMPLETE',
      tournamentId,
      tournamentName: tournament.name,
      roundId,
      roundNumber: round.roundNumber,
      roundLabel: round.label,
      advancingCount: advancingItemIds.length,
      eliminatedCount: eliminatedItemIds.length,
      recipientIds: [
        tournament.facilitatorId,
        ...(tournament.config.defaultParticipantIds ?? []),
      ],
    });

    if (tournament.config.autoAdvance && updatedTournament.state !== 'COMPLETE') {
      // Open the newly created next round
      const nextRound = get()
        .getRoundsForTournament(tournamentId)
        .find((r) => r.roundNumber === round.roundNumber + 1);
      if (nextRound) {
        await get().openRound(tournamentId, nextRound.id);
      }
    }
  },

  skipRound: async (tournamentId, roundId, rationale) => {
    const round = get().rounds[roundId];
    if (!round) throw new Error('Round not found');
    const now = new Date();
    const updated: TournamentRound = { ...round, state: 'SKIPPED', updatedAt: now };
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      await supabase.from('tournament_rounds').update({ state: 'SKIPPED', updated_at: now.toISOString() }).eq('id', roundId);
      const t = get().tournaments[tournamentId];
      if (t) {
        await supabase.from('tournament_audit_events').insert({
          tournament_id: tournamentId,
          round_id: roundId,
          event_type: 'ROUND_SKIPPED',
          actor_id: t.facilitatorId,
          payload: { rationale },
          created_at: now.toISOString(),
        });
      }
    }
    set((s) => ({ rounds: { ...s.rounds, [roundId]: updated } }));
  },

  // ---- Groups ----

  updateGroupItems: async (groupId, itemIds) => {
    const group = get().groups[groupId];
    if (!group) throw new Error('Group not found');
    const round = get().rounds[group.roundId];
    if (round?.state !== 'PENDING') {
      throw new Error('Can only update group items when round is PENDING');
    }
    const updated: TournamentGroup = {
      ...group,
      itemIds,
      updatedAt: new Date(),
    };
    set((s) => ({ groups: { ...s.groups, [groupId]: updated } }));
    return updated;
  },

  updateGroupParticipants: async (groupId, participantIds) => {
    const group = get().groups[groupId];
    if (!group) throw new Error('Group not found');
    const updated: TournamentGroup = {
      ...group,
      participantIds,
      updatedAt: new Date(),
    };
    set((s) => ({ groups: { ...s.groups, [groupId]: updated } }));
    return updated;
  },

  updateRoundParticipants: async (roundId, participantIds) => {
    const round = get().rounds[roundId];
    if (!round) throw new Error('Round not found');
    const now = new Date();
    const updated: TournamentRound = { ...round, participantIds, updatedAt: now };
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error } = await supabase
        .from('tournament_rounds')
        .update({ participant_ids: participantIds, updated_at: now.toISOString() })
        .eq('id', roundId);
      if (error) {
        console.error('[TournamentStore] Failed to update round participants:', error.message);
        dispatchStoreError('Tournament', 'update round participants', error.message);
        return;
      }
    }
    set((s) => ({ rounds: { ...s.rounds, [roundId]: updated } }));
  },

  updateTournamentParticipants: async (tournamentId, mode, defaultParticipantIds) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament) throw new Error('Tournament not found');
    const now = new Date();
    const updatedConfig = {
      ...tournament.config,
      participantMode: mode,
      defaultParticipantIds,
    };
    const updated: Tournament = { ...tournament, config: updatedConfig, updatedAt: now };
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error } = await supabase
        .from('tournaments')
        .update({ config: updatedConfig, updated_at: now.toISOString() })
        .eq('id', tournamentId);
      if (error) {
        console.error('[TournamentStore] Failed to update tournament participants:', error.message);
        dispatchStoreError('Tournament', 'update tournament participants', error.message);
        return;
      }
    }
    set((s) => ({ tournaments: { ...s.tournaments, [tournamentId]: updated } }));
  },

  // ---- Results / Export ----

  exportFinalResult: async (tournamentId) => {
    const tournament = get().tournaments[tournamentId];
    if (!tournament || tournament.state !== 'COMPLETE') {
      throw new Error('Tournament is not complete');
    }
    const ranked = tournament.finalRankedItemIds ?? [];
    if (ranked.length === 0) throw new Error('The tournament has no final result to export.');
    if (tournament.publishedPrioritySetId) return tournament.publishedPrioritySetId;
    if (!tournament.cycleId || !tournament.trackId) {
      throw new Error('A priority list belongs to a cycle’s track, and this tournament has none.');
    }

    // S-14: the final result becomes a proposed priority list on the
    // tournament's track, through the priority list's own write path. It is
    // then decided, ratified and published there like any other list —
    // exporting does not skip that governance.
    const { usePrioritySetStore } = await import('./prioritySetStore');
    const { useUserStore } = await import('./userStore');
    const created = await usePrioritySetStore.getState().createPrioritySet({
      cycleId: tournament.cycleId,
      trackId: tournament.trackId,
      loopId: undefined as unknown as string,
      name: `${tournament.name} — final result`,
      description: `The final order of the tournament “${tournament.name}”.`,
      state: 'proposed',
      itemIds: ranked,
      rankedItemIds: ranked,
      proposedBy: useUserStore.getState().userId ?? undefined,
    } as Parameters<ReturnType<typeof usePrioritySetStore.getState>['createPrioritySet']>[0]);
    if (!created) {
      throw new Error(usePrioritySetStore.getState().error ?? 'The priority list could not be created.');
    }

    const now = new Date();
    const supabase = getSafeSupabaseClient();
    if (supabase && isSupabaseAvailable()) {
      const { error } = await supabase
        .from('tournaments')
        .update({ published_priority_set_id: created.id, updated_at: now.toISOString() })
        .eq('id', tournamentId);
      if (error) {
        dispatchStoreError('Tournament', 'link the exported priority list', error.message);
      }
    }
    const updated: Tournament = { ...tournament, publishedPrioritySetId: created.id, updatedAt: now };
    set((s) => ({ tournaments: { ...s.tournaments, [tournamentId]: updated } }));
    return created.id;
  },

  // ---- Notifications ----

  dispatchNotification: (payload) => {
    // S-14: through the notifications engine (in-app, and whatever channels the
    // 'custom' event carries). Tournaments have no event of their own in the
    // register, so the words say which moment this is.
    const recipients = [...new Set(payload.recipientIds.filter(Boolean))];
    if (recipients.length === 0) return;
    const { title, body } = tournamentNotificationWords(payload);
    void import('../../lib/notificationService').then(({ sendNotification, DEFAULT_EVENT_CHANNELS }) =>
      sendNotification({
        event: 'custom',
        channels: DEFAULT_EVENT_CHANNELS.custom,
        recipientIds: recipients,
        title,
        body,
        priority: payload.event === 'ADVANCEMENT_AWAITING_CONFIRMATION' ? 'high' : 'normal',
        data: { tournamentId: payload.tournamentId, roundId: payload.roundId, tournamentEvent: payload.event },
        actionUrl: `/prioritization/tournaments/${payload.tournamentId}`,
        actionLabel: 'Open the tournament',
      }).catch((err: unknown) => console.error('[TournamentStore] notification not sent:', err)),
    );
  },
}));

// ============================================================================
// Standalone helper: persist session IDs onto a round after session creation
// Called from TournamentDetail when a session is created for a round.
// ============================================================================

export async function persistRoundSessionIds(roundId: string, sessionIds: string[]): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || !isSupabaseAvailable()) return;
  await supabase
    .from('tournament_rounds')
    .update({ session_ids: sessionIds, updated_at: new Date().toISOString() })
    .eq('id', roundId);
}

// ============================================================================
// S-14 helpers: prerequisite checks, a single session's result, notice words
// ============================================================================

/** The words for a tournament notification. */
export function tournamentNotificationWords(p: TournamentNotificationPayload): { title: string; body: string } {
  const round = p.roundLabel ?? (p.roundNumber != null ? `Round ${p.roundNumber}` : 'A round');
  switch (p.event) {
    case 'TOURNAMENT_CREATED':
      return { title: `Tournament: ${p.tournamentName}`, body: `You are part of the tournament “${p.tournamentName}”.` };
    case 'ROUND_OPENED':
      return { title: `${round} is open`, body: `${round} of “${p.tournamentName}” is open for your input.` };
    case 'ROUND_SESSIONS_COMPLETE':
      return { title: `${round} is in`, body: `Every session in ${round.toLowerCase()} of “${p.tournamentName}” has published.` };
    case 'ROUND_COMPLETE':
      return {
        title: `${round} is done`,
        body: `${round} of “${p.tournamentName}” is done${p.advancingCount != null ? `: ${p.advancingCount} advance` : ''}${p.eliminatedCount != null ? `, ${p.eliminatedCount} are out` : ''}.`,
      };
    case 'ADVANCEMENT_AWAITING_CONFIRMATION':
      return { title: `Confirm who advances`, body: `${round} of “${p.tournamentName}” is in. Confirm which items advance.` };
    case 'TOURNAMENT_COMPLETE':
      return { title: `Tournament finished`, body: `“${p.tournamentName}” has its final result.` };
    case 'TOURNAMENT_CANCELLED':
      return { title: `Tournament cancelled`, body: `“${p.tournamentName}” was cancelled.` };
    default:
      return { title: p.tournamentName, body: `There is news on the tournament “${p.tournamentName}”.` };
  }
}

/** The items competing in a round: its groups', else the prior round's survivors, else the tournament's. */
function roundItemIds(
  state: { getRoundsForTournament: (id: string) => TournamentRound[]; tournaments: Record<string, Tournament> },
  tournamentId: string,
  round: TournamentRound,
): string[] {
  const grouped = (round.groups ?? []).flatMap((g) => g.itemIds ?? []);
  if (grouped.length > 0) return [...new Set(grouped)];
  const prior = state.getRoundsForTournament(tournamentId).find((r) => r.roundNumber === round.roundNumber - 1);
  if (prior?.advancingItemIds?.length) return prior.advancingItemIds;
  return state.tournaments[tournamentId]?.itemIds ?? [];
}

/** A single-session round's saved result, read from the session. */
async function singleSessionResult(
  sessionId: string | undefined,
): Promise<{ rankedItemIds: string[]; scores: Record<string, number> } | null> {
  if (!sessionId) return null;
  const { useSessionStore } = await import('./sessionStore');
  let session = useSessionStore.getState().sessions.find((x) => x.id === sessionId);
  if (!session?.rankingOutput) {
    await useSessionStore.getState().fetchSessionById(sessionId);
    session = useSessionStore.getState().sessions.find((x) => x.id === sessionId);
  }
  const output = session?.rankingOutput;
  if (!output?.rankedItemIds?.length) return null;
  const scores: Record<string, number> = {};
  for (const [id, score] of Object.entries(output.scores ?? {})) {
    const total = (score as { total?: unknown })?.total;
    if (typeof total === 'number') scores[id] = total;
  }
  return { rankedItemIds: output.rankedItemIds, scores };
}

/**
 * Whether one of the database-backed prerequisites holds (S-14). Fails closed:
 * anything that cannot be read is unmet.
 *  - ITEM_READINESS: the share of the round's items that are ready for ranking
 *    is at least the threshold (default 100%).
 *  - VALUES_SUFFICIENT: every item has an accepted (official) value for every
 *    named attribute.
 *  - PARTICIPANT_CONFIRMED: every named person is an active participant of the
 *    round's sessions.
 */
async function checkRoundPrerequisite(
  prereq: RoundPrerequisite,
  round: TournamentRound,
  itemIds: string[],
): Promise<boolean> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || !isSupabaseAvailable()) return false;
  try {
    if (prereq.type === 'VALUES_SUFFICIENT') {
      const keys = prereq.requiredAttributeKeys ?? [];
      if (keys.length === 0 || itemIds.length === 0) return keys.length === 0;
      const { data, error } = await supabase
        .from('accepted_values')
        .select('item_id, attribute_key')
        .in('item_id', itemIds)
        .in('attribute_key', keys)
        .is('session_scope', null);
      if (error) return false;
      const have = new Set((data ?? []).map((r: { item_id: string; attribute_key: string }) => `${r.item_id}|${r.attribute_key}`));
      return itemIds.every((id) => keys.every((k) => have.has(`${id}|${k}`)));
    }
    if (prereq.type === 'ITEM_READINESS') {
      if (itemIds.length === 0) return true;
      const [{ useItemsStore }, { computeItemReadiness }] = await Promise.all([
        import('./itemsStore'),
        import('../../lib/itemReadiness'),
      ]);
      const items = useItemsStore.getState().items.filter((i) => itemIds.includes(i.id));
      if (items.length < itemIds.length) return false;
      const ready = items.filter((i) => computeItemReadiness(i as Parameters<typeof computeItemReadiness>[0]).isReady).length;
      const threshold = prereq.readinessThreshold ?? 100;
      return (ready / itemIds.length) * 100 >= threshold;
    }
    if (prereq.type === 'PARTICIPANT_CONFIRMED') {
      const people = prereq.requiredParticipantIds ?? [];
      if (people.length === 0) return true;
      const sessionIds = round.sessionIds ?? [];
      if (sessionIds.length === 0) return false;
      const { data, error } = await supabase
        .from('session_participants')
        .select('user_id')
        .in('session_id', sessionIds)
        .in('user_id', people)
        .eq('status', 'active');
      if (error) return false;
      const confirmed = new Set((data ?? []).map((r: { user_id: string }) => r.user_id));
      return people.every((id) => confirmed.has(id));
    }
  } catch {
    return false;
  }
  return false;
}

